import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { UserCog, Save, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";

export function useWarehouseLeaders() {
  return useQuery({ queryKey: ["scm-warehouse-leaders"], queryFn: async () => (await api.get("/scm/settings/warehouse-leaders")).data, staleTime: 60000 });
}

/** Admin editor: one leader (name + e-mails + weekly toggle) per warehouse / plant. Stored in SCM settings for all users. */
export function WarehouseLeaders({ isAdmin }) {
  const qc = useQueryClient();
  const q = useWarehouseLeaders();
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState({});
  useEffect(() => { if (q.data) setRows(Object.fromEntries(Object.entries(q.data).map(([w, v]) => [w, { name: v.name || "", emails: (v.emails || []).join(", "), weekly: v.weekly !== false }]))); }, [q.data]);
  if (!q.data || !Object.keys(q.data).length) return null;
  const configured = Object.values(q.data).filter((v) => v.emails?.length).length;
  const save = async () => {
    try { await api.put("/scm/settings", { warehouse_leaders: rows }); toast.success("Location leaders saved — e-mails are pre-filled for everyone and used for the Monday 08:30 inventory mail"); qc.invalidateQueries({ queryKey: ["scm-warehouse-leaders"] }); qc.invalidateQueries({ queryKey: ["scm-settings"] }); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <section className="pp-card p-3 text-xs" data-testid="warehouse-leaders">
      <button className="flex w-full items-center justify-between gap-2 text-left" onClick={() => setOpen((o) => !o)} data-testid="warehouse-leaders-toggle">
        <div className="pp-label flex items-center gap-2"><UserCog size={14} /> Location leaders · {configured} of {Object.keys(q.data).length} warehouse(s) have a leader · weekly inventory mail Mon 08:30 IST</div>
        {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>
      {open && <div className="mt-3 space-y-2">
        <div className="grid grid-cols-[110px_1fr_2fr_90px] gap-2 pp-label text-[10px]"><span>Warehouse</span><span>Leader name</span><span>E-mail(s) · comma separated</span><span>Weekly mail</span></div>
        {Object.entries(rows).map(([w, v]) => (
          <div key={w} className="grid grid-cols-[110px_1fr_2fr_90px] items-center gap-2" data-testid={`leader-row-${w}`}>
            <span className="font-mono font-bold">{w}</span>
            <input className="pp-input h-8 text-xs" placeholder="Store lead" value={v.name} disabled={!isAdmin} onChange={(e) => setRows({ ...rows, [w]: { ...v, name: e.target.value } })} data-testid={`leader-name-${w}`} />
            <input className="pp-input h-8 text-xs" placeholder="lead@company.com, plant.head@company.com" value={v.emails} disabled={!isAdmin} onChange={(e) => setRows({ ...rows, [w]: { ...v, emails: e.target.value } })} data-testid={`leader-emails-${w}`} />
            <label className="inline-flex items-center gap-1.5"><input type="checkbox" checked={v.weekly} disabled={!isAdmin} onChange={(e) => setRows({ ...rows, [w]: { ...v, weekly: e.target.checked } })} data-testid={`leader-weekly-${w}`} /> Monday</label>
          </div>
        ))}
        <div className="flex items-center justify-between gap-2 pt-1">
          <span className="text-slate-500">Leaders are pre-filled in each warehouse's E-mail button for every user. Switch the whole weekly mail on/off under Settings → E-mail Recipients → "Weekly warehouse inventory".</span>
          {isAdmin && <button className="pp-btn-primary h-8 px-3" onClick={save} data-testid="warehouse-leaders-save"><Save size={12} /> Save leaders</button>}
        </div>
      </div>}
    </section>
  );
}
